/**
 * «Сводка» проекта КСП: что показать на первом экране и что требует внимания.
 * Модуль без импортов значений (только типы) — проверяется node:test:
 *   cd frontend && npm run test:ksp
 * Подписи кодов приходят параметром `label`, чтобы модуль не тянул алиасы Vite.
 */

export type Tone = 'neutral' | 'ok' | 'warn' | 'bad';
export type Severity = 'bad' | 'warn' | 'info';

export interface PulseKpi {
  code: string;
  fact_pct: string | null;
  plan_pct: string | null;
  delta_pp: string | null;
  status: string;
  reasons?: string[];
  reason?: string;
}

export interface PulseInput {
  summary: {
    forecast_finish: string | null;
    contract_finish: string | null;
    finish_deviation_calendar_days: number | null;
    kpis: PulseKpi[];
    quality: { reasons: string[] };
    calculated_at: string;
  } | null;
  milestones: {
    title: string;
    contractual_due_at: string;
    forecast_finish?: string | null;
    achievement_state: string;
  }[];
  activities: {
    code: string;
    name: string;
    kind?: string;
    forecast_finish?: string | null;
    total_float_minutes?: number;
  }[];
  actual_submissions: { status: string; performed_date: string }[];
  operational?: { open_milestone_alert_count: number } | null;
  source_health?: { source: string; status: string; reason?: string }[];
}

export interface FinishStatus {
  tone: Tone;
  value: string;
  forecast: string | null;
  contract: string | null;
}

const DAY = 86400000;

export function plural(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return many;
  if (last === 1) return one;
  if (last >= 2 && last <= 4) return few;
  return many;
}

/** Успеваем ли к договорному сроку: «Запас 109 дн.» / «Отставание 12 дн.» / «В срок». */
export function finishStatus(summary: PulseInput['summary']): FinishStatus {
  if (!summary) return { tone: 'neutral', value: 'Расчёта нет', forecast: null, contract: null };
  const forecast = summary.forecast_finish;
  const contract = summary.contract_finish;
  let deviation = summary.finish_deviation_calendar_days;
  if (deviation == null && forecast && contract) {
    deviation = Math.round((Date.parse(forecast) - Date.parse(contract)) / DAY);
  }
  if (deviation == null)
    return {
      tone: 'neutral',
      value: forecast ? 'Срок не задан' : 'Прогноза нет',
      forecast,
      contract,
    };
  if (deviation < 0)
    return { tone: 'ok', value: `Запас ${Math.abs(deviation)} дн.`, forecast, contract };
  if (deviation > 0)
    return { tone: 'bad', value: `Отставание ${deviation} дн.`, forecast, contract };
  return { tone: 'ok', value: 'В срок', forecast, contract };
}

/** Физическое выполнение проекта: процент или причина, почему его нет. */
export function progress(
  summary: PulseInput['summary'],
  label: (code: string) => string,
): { value: string | null; plan: string | null; delta: string | null; reason: string | null } {
  const kpi = summary?.kpis.find((item) => item.code.replace(/^ksp\./, '') === 'physical_progress');
  if (!kpi) return { value: null, plan: null, delta: null, reason: summary ? 'показатель не рассчитан' : null };
  const reasons = kpi.reasons ?? (kpi.reason ? [kpi.reason] : []);
  return {
    value: kpi.fact_pct,
    plan: kpi.plan_pct,
    delta: kpi.delta_pp,
    reason: kpi.fact_pct == null && reasons.length ? label(reasons[0]) : null,
  };
}

export interface MilestonePoint {
  title: string;
  date: string;
  kind: 'contract' | 'calculated';
  achieved: boolean;
}

/**
 * Вехи для ленты: договорные; если их нет — ближайшие расчётные вехи графика
 * (работы вида «веха») от сегодняшнего дня, не больше `limit`.
 */
export function milestonePoints(input: PulseInput, now: Date, limit = 6): MilestonePoint[] {
  if (input.milestones.length) {
    return input.milestones
      .map((m) => ({
        title: m.title,
        date: m.forecast_finish ?? m.contractual_due_at,
        kind: 'contract' as const,
        achieved: m.achievement_state === 'achieved',
      }))
      .filter((m) => Number.isFinite(Date.parse(m.date)))
      .sort((a, b) => Date.parse(a.date) - Date.parse(b.date));
  }
  const from = now.getTime() - DAY;
  return input.activities
    .filter((a) => a.kind === 'milestone' && a.forecast_finish && Date.parse(a.forecast_finish) >= from)
    .sort((a, b) => Date.parse(a.forecast_finish!) - Date.parse(b.forecast_finish!))
    .slice(0, limit)
    .map((a) => ({
      title: `${a.code} · ${a.name}`,
      date: a.forecast_finish!,
      kind: 'calculated' as const,
      achieved: false,
    }));
}

/** Ближайшая веха от сегодняшнего дня (договорная или расчётная). */
export function nextMilestone(input: PulseInput, now: Date): MilestonePoint | null {
  const points = milestonePoints(input, now, 50).filter(
    (m) => !m.achieved && Date.parse(m.date) >= now.getTime() - DAY,
  );
  return points[0] ?? null;
}

/** Факт за последние 7 дней и ожидающие приёмки. */
export function factActivity(input: PulseInput, now: Date): { week: number; pending: number } {
  const from = now.getTime() - 7 * DAY;
  return {
    week: input.actual_submissions.filter((s) => {
      const t = Date.parse(s.performed_date);
      return Number.isFinite(t) && t >= from && t <= now.getTime() + DAY;
    }).length,
    pending: input.actual_submissions.filter((s) => s.status === 'pending').length,
  };
}

export interface AttentionItem {
  key: string;
  severity: Severity;
  title: string;
  detail?: string;
  /** Вкладка, где это исправляют (`?tab=`). */
  tab: string;
}

/** Причина в расчёте → важность, вкладка и подсказка, что сделать. */
const REASONS: Record<string, { severity: Severity; tab: string; hint: string }> = {
  negative_float: { severity: 'bad', tab: 'schedule', hint: 'работы выходят за договорные или директивные сроки' },
  constraint_violation: { severity: 'bad', tab: 'schedule', hint: 'проверьте ограничения сроков у работ графика' },
  approved_baseline_missing: { severity: 'warn', tab: 'schedule', hint: 'отклонения не с чем сравнить — утвердите версию графика' },
  approved_weights_missing: { severity: 'warn', tab: 'schedule', hint: 'процент выполнения проекта не считается' },
  weights_not_normalized: { severity: 'warn', tab: 'schedule', hint: 'процент выполнения может быть неверным' },
  monthly_plan_missing: { severity: 'warn', tab: 'periods', hint: 'выполнение месяца не с чем сравнить' },
  period_not_released: { severity: 'warn', tab: 'periods', hint: 'задание не уйдёт прорабам, пока его не выпустить' },
  reporting_gap: { severity: 'warn', tab: 'actual', hint: 'прогноз опирается на неполный факт' },
  unmapped_accepted_fact: { severity: 'warn', tab: 'actual', hint: 'часть факта не попала в работы графика' },
  started_remaining_unknown: { severity: 'warn', tab: 'schedule', hint: 'сроки начатых работ считаются по плану' },
  assumed_supply: { severity: 'info', tab: 'sources', hint: 'материалы приняты в наличии без графика поставок' },
  expected_rd_catalogue_unknown: { severity: 'info', tab: 'sources', hint: 'задайте перечень комплектов РД в данных проекта' },
  partial_rd_catalogue: { severity: 'info', tab: 'sources', hint: 'полный график нельзя утвердить у Заказчика' },
  rd_catalogue_needs_review: { severity: 'info', tab: 'sources', hint: 'проверьте перечень РД' },
};

const ORDER: Record<Severity, number> = { bad: 0, warn: 1, info: 2 };

/** Что мешает расчёту или требует решения — от важного к справочному. */
export function attention(
  input: PulseInput,
  label: (code: string) => string,
  now: Date,
): AttentionItem[] {
  const items: AttentionItem[] = [];
  const s = input.summary;
  if (!s) {
    items.push({
      key: 'no_summary',
      severity: 'bad',
      title: 'Расчёт не опубликован',
      detail: 'Руководитель не видит цифры КСП: рассчитайте и опубликуйте график',
      tab: 'schedule',
    });
  }
  const alerts = input.operational?.open_milestone_alert_count ?? 0;
  if (alerts > 0)
    items.push({
      key: 'alerts',
      severity: 'bad',
      title: `${alerts} ${plural(alerts, 'предупреждение', 'предупреждения', 'предупреждений')} о просрочке вех`,
      detail: 'закрываются только письмом Заказчику с причинами',
      tab: 'milestones',
    });

  const negative = input.activities.filter((a) => (a.total_float_minutes ?? 0) < 0);
  const worst = negative.reduce<number | null>(
    (min, a) => (min == null || a.total_float_minutes! < min ? a.total_float_minutes! : min),
    null,
  );
  const reasons = new Set<string>([
    ...(s?.quality.reasons ?? []),
    ...(s?.kpis.flatMap((k) => k.reasons ?? (k.reason ? [k.reason] : [])) ?? []),
  ]);
  if (negative.length && !reasons.has('negative_float')) reasons.add('negative_float');
  for (const code of reasons) {
    const known = REASONS[code];
    let detail = known?.hint;
    if (code === 'negative_float' && negative.length) {
      const days = worst == null ? null : Math.round(Math.abs(worst) / 1440);
      detail = `${negative.length} ${plural(negative.length, 'работа', 'работы', 'работ')} с отрицательным резервом${days ? `, худший −${days} дн.` : ''}`;
    }
    items.push({
      key: `reason:${code}`,
      severity: known?.severity ?? 'warn',
      title: label(code),
      detail,
      tab: known?.tab ?? 'overview',
    });
  }

  if (input.milestones.length === 0) {
    const calculated = input.activities.filter((a) => a.kind === 'milestone').length;
    items.push({
      key: 'no_milestones',
      severity: 'warn',
      title: 'Договорные вехи не заведены',
      detail: calculated
        ? `предупреждения о просрочке не придут; в графике ${calculated} ${plural(calculated, 'расчётная веха', 'расчётные вехи', 'расчётных вех')}`
        : 'предупреждения о просрочке не придут',
      tab: 'milestones',
    });
  }
  const { pending } = factActivity(input, now);
  if (pending > 0)
    items.push({
      key: 'pending_fact',
      severity: 'warn',
      title: `${pending} ${plural(pending, 'факт ждёт', 'факта ждут', 'фактов ждут')} приёмки`,
      detail: 'непринятый факт не попадает в прогноз',
      tab: 'actual',
    });
  for (const source of input.source_health ?? []) {
    if (source.status === 'ok') continue;
    items.push({
      key: `source:${source.source}`,
      severity: 'info',
      title: `Источник «${label(source.source)}»: ${label(source.status).toLowerCase()}`,
      detail: source.reason,
      tab: 'sources',
    });
  }
  return items.sort((a, b) => ORDER[a.severity] - ORDER[b.severity]);
}

export interface CurvePoint {
  date: string;
  plan: number | null;
  fact: number | null;
  forecast: number | null;
}

/** Точки накопительной кривой для графика: строки-десятичные → числа, пустое — пусто. */
export function curvePoints(
  points: { date: string; baseline: string | null; actual: string | null; forecast: string | null }[],
): CurvePoint[] {
  const num = (v: string | null) => (v == null || v === '' ? null : Number(v));
  return points.map((p) => ({
    date: p.date,
    plan: num(p.baseline),
    fact: num(p.actual),
    forecast: num(p.forecast),
  }));
}
